// Data access for the USER SE QR Issue page (PRD Dashboard SE – QR Codes,
// sections 1–5). The Dealer → POS shipment history is served by the `target`
// param of `GET /api/qrs/issue`. The page highlights come from a single
// `GET /api/qrs/issue/highlight` call (no `target`) that returns the Dealer →
// POS shipment counts and the QR counts of both flows. The Dealer → POS recap
// comes from `GET /api/qrs/issue/intransit-pos` (INTRANSIT) and
// `GET /api/qrs/issue/received-pos` (RECEIVED); both are served and searched
// server-side.

const INVALID_RESPONSE_MESSAGE = "Format respons pengiriman QR tidak valid";

export const FLOW_MD_DEALER = "md_dealer";
export const FLOW_DEALER_POS = "dealer_pos";

// Who the history is loaded for. USER SE reads the dedicated `/se/*`
// endpoints; every other role keeps the `target` param of `/api/qrs/issue`.
export const AUDIENCE_SE = "se";
export const AUDIENCE_DEFAULT = "default";

// Maps each UI flow to the `target` param of `GET /api/qrs/issue`.
const ISSUE_TARGETS = {
  [FLOW_MD_DEALER]: "dealer",
  [FLOW_DEALER_POS]: "pos",
};

// The ONLY place a UI flow is mapped to its USER SE history endpoint.
const SE_ISSUE_ENDPOINTS = {
  [FLOW_MD_DEALER]: "/api/qrs/issue/se/dealer",
  [FLOW_DEALER_POS]: "/api/qrs/issue/se/pos",
};

// Sort param names of the default `GET /api/qrs/issue`, by column key.
const DEFAULT_SORT_KEYS = {
  deliveredAt: "delivery_at",
  qty: "qty",
  ahmId: "ahm_id",
  dealerCode: "kode_dealer",
  dealerName: "dealer",
  posCode: "kode_dealer_pos",
  posName: "pos_name",
  city: "city",
  dealerType: "dealer_type",
  status: "status",
  bastNo: "bast_no",
  senderName: "sender_name",
  receivedAt: "received_at",
};

// Sort param names shared by both USER SE endpoints, by column key.
const SE_SORT_KEYS = {
  deliveredAt: "delivery_at",
  qty: "qty",
  status: "status",
  bastNo: "bast_no",
  senderName: "sender_name",
  receivedAt: "received_at",
  ahmId: "dealer_ahm_id",
  dealerCode: "dealer_code",
  dealerName: "dealer_name",
  city: "dealer_city",
  dealerType: "dealer_type",
};

// Sort param names only the USER SE Dealer → POS endpoint accepts.
const SE_POS_SORT_KEYS = {
  posCode: "pos_code",
  posName: "pos_name",
};

// Sortable columns of the USER SE endpoints, by flow.
const SE_SORT_KEYS_BY_FLOW = {
  [FLOW_MD_DEALER]: SE_SORT_KEYS,
  [FLOW_DEALER_POS]: { ...SE_SORT_KEYS, ...SE_POS_SORT_KEYS },
};

// The only place that decides which endpoint serves a POS recap status.
const POS_RECAP_ENDPOINTS = {
  INTRANSIT: "/api/qrs/issue/intransit-pos",
  RECEIVED: "/api/qrs/issue/received-pos",
};

// Dealer codes are a letter followed by digits (e.g. "A0160"). A POS code is
// "P" + 2 digits + its parent dealer code (e.g. "P01A0160"), so this pattern
// is the only way to tell a POS apart from a dealer in `/api/master/dealer`.
const POS_CODE_PATTERN = /^P\d{2}[A-Z]/i;

function toCount(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function compactParams(params) {
  const result = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      result[key] = value;
    }
  });
  return result;
}

/**
 * Reads the `intransit` and `received` counts of one highlight block.
 * @param {unknown} block Highlight sub-object, e.g. `{ intransit, received }`.
 * @returns {{ intransit: number, received: number } | null} `null` when the
 *   block is missing or not an object, so callers never invent a 0.
 */
function parseHighlightBlock(block) {
  if (!block || typeof block !== "object") return null;
  return {
    intransit: toCount(block.intransit),
    received: toCount(block.received),
  };
}

/**
 * Extracts the page highlights from the `GET /api/qrs/issue/highlight` body.
 * One call returns all three blocks; the top-level `sent`, `received` and
 * `intransit` fields are ignored.
 * - `posShipment`: Dealer → POS shipment counts (`pos_shipment`).
 * - `dealerQr`: MD → Dealer QR counts (`dealer_qr`).
 * - `posQr`: Dealer → POS QR counts (`pos_qr`).
 * @param {unknown} body Response body, e.g.
 *   `{ data: { dealer_qr, pos_qr, pos_shipment } }`.
 * @returns {{
 *   posShipment: { intransit: number, received: number } | null,
 *   dealerQr: { intransit: number, received: number } | null,
 *   posQr: { intransit: number, received: number } | null,
 * }} A block is `null` when the backend omitted it.
 */
export function parseIssueHighlight(body) {
  const data = body ? body.data : undefined;
  if (!data || typeof data !== "object") {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }
  return {
    posShipment: parseHighlightBlock(data.pos_shipment),
    dealerQr: parseHighlightBlock(data.dealer_qr),
    posQr: parseHighlightBlock(data.pos_qr),
  };
}

/**
 * Fetches the page highlights (shipment counts and QR counts) for the
 * selected period in a single call.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @param {{ startDate: string, endDate: string }} query
 * @returns {Promise<ReturnType<typeof parseIssueHighlight>>}
 */
export async function fetchIssueHighlight(http, { startDate, endDate }) {
  const params = compactParams({
    start_date: startDate,
    end_date: endDate,
  });
  const resp = await http.get("/api/qrs/issue/highlight", { params });
  return parseIssueHighlight(resp.data);
}

/**
 * Adds the total to a highlight block. The total is computed as
 * `intransit + received` (per the PRD), not read from the backend.
 * @param {{ intransit: number, received: number } | null} stat
 * @returns {{ total: number, intransit: number, received: number } | null}
 *   `null` when `stat` is `null`.
 */
export function withTotal(stat) {
  if (!stat) return null;
  return {
    total: stat.intransit + stat.received,
    intransit: stat.intransit,
    received: stat.received,
  };
}

function parseIssueRow(item) {
  if (!item || typeof item !== "object") {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }
  return {
    id: item.id,
    deliveredAt: item.delivery_at || null,
    qty: toCount(item.qty),
    ahmId: item.ahm_id || "",
    dealerCode: item.kode_dealer || "",
    dealerName: item.dealer || "",
    posCode: item.kode_dealer_pos || "",
    posName: item.pos_name || "",
    city: item.city || "",
    dealerType: item.dealer_type || "",
    status: item.status || "",
    bastNo: item.bast_no || "",
    senderName: item.sender_name || "",
    receivedAt: item.received_at || null,
    // The edit modal still reads the original snake_case fields.
    raw: item,
  };
}

// Maps one row of `GET /api/qrs/issue/se/{dealer,pos}` to the same shape as
// `parseIssueRow`. The city and network type are the dealer's; the POS
// city/type are intentionally not used as a fallback.
function parseSeIssueRow(item) {
  if (!item || typeof item !== "object") {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }
  return {
    id: item.id,
    deliveredAt: item.delivery_at || null,
    qty: toCount(item.qty),
    ahmId: item.dealer_ahm_id || "",
    dealerCode: item.dealer_code || "",
    dealerName: item.dealer_name || "",
    posCode: item.pos_code || "",
    posName: item.pos_name || "",
    city: item.dealer_city || "",
    dealerType: item.dealer_type || "",
    status: item.status || "",
    bastNo: item.bast_no || "",
    senderName: item.sender_name || "",
    receivedAt: item.received_at || null,
    raw: item,
  };
}

/**
 * Extracts one page of shipment history from a `GET /api/qrs/issue` or
 * `GET /api/qrs/issue/se/{dealer,pos}` body.
 * @param {unknown} body Response body with `data` rows and pagination `meta`.
 * @param {(item: unknown) => IssueRow} [parseRow] Row mapper; defaults to the
 *   mapper of `GET /api/qrs/issue`.
 * @returns {{ rows: IssueRow[], total: number, lastPage: number }}
 */
export function parseIssuePage(body, parseRow = parseIssueRow) {
  const data = body ? body.data : undefined;
  const meta = body ? body.meta : undefined;
  if (!Array.isArray(data) || !meta) {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }
  return {
    rows: data.map((item) => parseRow(item)),
    total: toCount(meta.total),
    lastPage: toCount(meta.lastPage),
  };
}

/**
 * Returns the sort param name of a table column.
 * @param {string} audience `AUDIENCE_SE` or `AUDIENCE_DEFAULT`.
 * @param {string} flow `FLOW_MD_DEALER` or `FLOW_DEALER_POS`.
 * @param {string} columnKey Row field shown by the column, e.g. `dealerCode`.
 * @returns {string | null} The `sort_by` value, or `null` when the column is
 *   not sortable for that audience and flow.
 */
export function issueSortKey(audience, flow, columnKey) {
  const keys =
    audience === AUDIENCE_SE ? SE_SORT_KEYS_BY_FLOW[flow] : DEFAULT_SORT_KEYS;
  return (keys && keys[columnKey]) || null;
}

/**
 * Fetches shipment history for one flow.
 * - Default audience: `GET /api/qrs/issue`, with the flow sent as `target`
 *   (`dealer` for MD → Dealer, `pos` for Dealer → POS) on every call.
 * - `AUDIENCE_SE`: `GET /api/qrs/issue/se/dealer` or `/se/pos`, with no
 *   `target`; `pos_code` is only sent for the Dealer → POS flow.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @param {{
 *   audience?: string, flow: string, startDate: string, endDate: string,
 *   dealerCode?: string, posCode?: string, status?: string, page: number,
 *   limit: number, sortBy?: string, sortOrder?: string,
 * }} query
 * @returns {Promise<ReturnType<typeof parseIssuePage>>}
 * @throws {Error} When `audience` is `AUDIENCE_SE` and `flow` is unknown;
 *   thrown before any request is made.
 */
export async function fetchIssuePage(http, query) {
  if (query.audience === AUDIENCE_SE) {
    const endpoint = SE_ISSUE_ENDPOINTS[query.flow];
    if (!endpoint) {
      throw new Error(`Alur pengiriman QR tidak dikenal: ${query.flow}`);
    }
    const seParams = compactParams({
      start_date: query.startDate,
      end_date: query.endDate,
      kode_dealer: query.dealerCode,
      pos_code: query.flow === FLOW_DEALER_POS ? query.posCode : undefined,
      status: query.status,
      page: query.page,
      limit: query.limit,
      sort_by: query.sortBy,
      sort_order: query.sortOrder,
    });
    const seResp = await http.get(endpoint, { params: seParams });
    return parseIssuePage(seResp.data, parseSeIssueRow);
  }
  const params = compactParams({
    target: ISSUE_TARGETS[query.flow] || ISSUE_TARGETS[FLOW_MD_DEALER],
    start_date: query.startDate,
    end_date: query.endDate,
    kode_dealer: query.dealerCode,
    kode_dealer_pos: query.posCode,
    status: query.status,
    page: query.page,
    limit: query.limit,
    sort_by: query.sortBy,
    sort_order: query.sortOrder,
  });
  const resp = await http.get("/api/qrs/issue", { params });
  return parseIssuePage(resp.data);
}

/**
 * Extracts POS options from the `GET /api/master/pos` body.
 * @param {unknown} body Response body, e.g. `{ data: [{ code, name }] }`.
 * @returns {{ code: string, name: string }[]}
 */
export function parsePosOptions(body) {
  const data = body ? body.data : undefined;
  if (!Array.isArray(data)) {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }
  return data
    .filter((item) => item && item.code)
    .map((item) => ({ code: String(item.code), name: item.name || "" }));
}

/**
 * Fetches the POS under one dealer.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @param {string} dealerCode Dealer code.
 * @returns {Promise<{ code: string, name: string }[]>}
 */
export async function fetchPosOptions(http, dealerCode) {
  const params = { kode_dealer: dealerCode };
  const resp = await http.get("/api/master/pos", { params });
  return parsePosOptions(resp.data);
}

function stripCodePrefix(name, code) {
  const prefix = `${code} - `;
  const text = name || "";
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
}

/**
 * Builds the dealer dropdown options from the `GET /api/master/dealer` rows
 * held in the store. The list also contains POS rows, which are dropped. The
 * store already prefixes each name with `"<code> - "`; that prefix is removed
 * so the dropdown label can show the code once.
 * @param {unknown} list Dealer rows, e.g. `[{ code, name }]`; may be null.
 * @returns {{ code: string, name: string }[]} Dealers only, `[]` when `list`
 *   is not an array.
 */
export function toDealerOptions(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((row) => row && row.code && !POS_CODE_PATTERN.test(String(row.code)))
    .map((row) => {
      const code = String(row.code);
      return { code, name: stripCodePrefix(row.name, code) };
    });
}

/**
 * Extracts the Dealer → POS recap from the `GET /api/qrs/issue/intransit-pos`
 * and `GET /api/qrs/issue/received-pos` bodies, which share one shape. Dealer `shipment` and `qty` are read from the API as-is, not re-summed
 * from their POS. The AHM ids and dealer/POS types are ignored.
 * @param {unknown} body Response body, e.g.
 *   `{ data: { summary: { pos, shipment, qty }, data: [dealer] } }` where each
 *   dealer carries `dealer_code`, `dealer_name`, `shipment`, `qty` and `pos`.
 * @returns {{
 *   chips: { pos: number, shipment: number, qty: number } | null,
 *   rows: {
 *     dealerCode: string, dealerName: string, shipment: number, qty: number,
 *     children: { posCode: string, posName: string, shipment: number, qty: number }[],
 *   }[],
 * }} `chips` is `null` when the backend omitted `summary`.
 */
export function parsePosRecap(body) {
  const data = body ? body.data : undefined;
  if (!data || !Array.isArray(data.data)) {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }
  const rows = data.data.map((dealer) => {
    if (!dealer || !Array.isArray(dealer.pos)) {
      throw new Error(INVALID_RESPONSE_MESSAGE);
    }
    return {
      dealerCode: dealer.dealer_code || "",
      dealerName: dealer.dealer_name || "",
      shipment: toCount(dealer.shipment),
      qty: toCount(dealer.qty),
      children: dealer.pos.map((pos) => ({
        posCode: (pos && pos.pos_code) || "",
        posName: (pos && pos.pos_name) || "",
        shipment: toCount(pos ? pos.shipment : 0),
        qty: toCount(pos ? pos.qty : 0),
      })),
    };
  });
  const summary = data.summary;
  const chips =
    summary && typeof summary === "object"
      ? {
          pos: toCount(summary.pos),
          shipment: toCount(summary.shipment),
          qty: toCount(summary.qty),
        }
      : null;
  return { chips, rows };
}

/**
 * Fetches the Dealer → POS recap for one status. The status picks the
 * endpoint and the backend applies the search keyword and all filters.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @param {{
 *   status: string, startDate?: string, endDate?: string, date?: string,
 *   year?: number | string, month?: number | string, search?: string,
 *   karesidenan?: string, city?: string, dealerCode?: string,
 *   dealerType?: string, dayDiffMin?: number | string,
 *   dayDiffMax?: number | string, senderOnly?: boolean | string,
 *   excludeChild?: boolean | string,
 * }} query `status` is `INTRANSIT` or `RECEIVED`; any other value throws
 *   before a request is made.
 * @returns {Promise<ReturnType<typeof parsePosRecap>>} The drawer chips
 *   (from the API summary, `null` if absent) and the dealer rows.
 */
export async function fetchPosRecap(http, query) {
  const endpoint = POS_RECAP_ENDPOINTS[query.status];
  if (!endpoint) {
    throw new Error(`Status rekap POS tidak dikenal: ${query.status}`);
  }
  const params = compactParams({
    start_date: query.startDate,
    end_date: query.endDate,
    date: query.date,
    year: query.year,
    month: query.month,
    search: (query.search || "").trim(),
    karesidenan: query.karesidenan,
    kota: query.city,
    kode_dealer: query.dealerCode,
    dealer_type: query.dealerType,
    day_diff_min: query.dayDiffMin,
    day_diff_max: query.dayDiffMax,
    sender_only: query.senderOnly,
    exclude_child: query.excludeChild,
  });
  const resp = await http.get(endpoint, { params });
  return parsePosRecap(resp.data);
}

/**
 * Sums a POS recap into the drawer chips.
 * @param {ReturnType<typeof parsePosRecap>["rows"]} summary Recap rows.
 * @returns {{ pos: number, shipment: number, qty: number }}
 */
export function toPosSummaryChips(summary) {
  return summary.reduce(
    (chips, dealer) => ({
      pos: chips.pos + dealer.children.length,
      shipment: chips.shipment + dealer.shipment,
      qty: chips.qty + dealer.qty,
    }),
    { pos: 0, shipment: 0, qty: 0 }
  );
}

/**
 * Builds the BAST PDF file name, e.g. `BAST_001-MPM-2026.pdf`.
 * @param {string} bastNo BAST code; "/" is replaced with "-".
 * @returns {string}
 */
export function toBastFilename(bastNo) {
  return `BAST_${String(bastNo || "").replace(/\//g, "-")}.pdf`;
}

/**
 * Formats a count with "." as the thousands separator, e.g. `10.000`.
 * @param {number} value Count to format.
 * @returns {string}
 */
export function formatCount(value) {
  return toCount(value).toLocaleString("id-ID");
}
